import React from 'react';
import { Box } from '@mui/material';

/**
 * Parses WhatsApp-style markdown and renders formatted React elements.
 *
 * Supported syntax:
 *   *bold*  _italic_  ~strikethrough~  ```code block```  `inline code`
 *   \n      line break
 *   * / -   bullet list items (line start)
 *   1.      numbered list items (line start)
 *   >       blockquote (line start)
 *   URLs    clickable links
 */

const URL_RE = /(https?:\/\/[^\s]+)/g;

// Inline formatting tokens ordered by specificity (code block before inline code).
const INLINE_RULES = [
  { re: /```([\s\S]*?)```/g,       tag: 'code',   block: true },
  { re: /`([^`]+)`/g,              tag: 'code',   block: false },
  { re: /\*([^*]+)\*/g,            tag: 'strong' },
  { re: /_([^_]+)_/g,              tag: 'em' },
  { re: /~([^~]+)~/g,              tag: 's' },
];

/* ------------------------------------------------------------------ */
/*  Inline parser – handles bold / italic / strike / code / links     */
/* ------------------------------------------------------------------ */

function parseInline(text, keyPrefix = '') {
  if (!text) return null;

  // 1) Split on URLs first — they must not be formatted further.
  const urlParts = text.split(URL_RE);
  const elements = [];

  urlParts.forEach((segment, idx) => {
    if (URL_RE.test(segment)) {
      elements.push(
        <a
          key={`${keyPrefix}u${idx}`}
          href={segment}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {segment}
        </a>
      );
      return;
    }

    // 2) Apply inline formatting rules sequentially.
    //    We tokenise the string by finding the earliest match, rendering it,
    //    then recursing into the remaining left / right parts.
    const formatted = applyInlineRules(segment, `${keyPrefix}s${idx}`);
    elements.push(...(Array.isArray(formatted) ? formatted : [formatted]));
  });

  return elements;
}

function applyInlineRules(text, keyPrefix) {
  if (!text) return [];

  for (const rule of INLINE_RULES) {
    rule.re.lastIndex = 0;
    const match = rule.re.exec(text);
    if (match) {
      const before = text.slice(0, match.index);
      const inner = match[1];
      const after = text.slice(match.index + match[0].length);
      const parts = [];

      if (before) parts.push(...applyInlineRules(before, `${keyPrefix}b`));

      if (rule.tag === 'code') {
        const sx = rule.block
          ? {
              display: 'block',
              bgcolor: 'action.selected',
              borderRadius: 1,
              padding: '6px 8px',
              fontFamily: 'monospace',
              fontSize: '0.85em',
              whiteSpace: 'pre-wrap',
              margin: '4px 0',
            }
          : {
              bgcolor: 'action.selected',
              borderRadius: 1,
              padding: '1px 4px',
              fontFamily: 'monospace',
              fontSize: '0.9em',
            };
        parts.push(
          <Box component="code" key={`${keyPrefix}m`} sx={sx}>
            {inner}
          </Box>
        );
      } else {
        const Tag = rule.tag;
        parts.push(
          <Tag key={`${keyPrefix}m`} style={{ margin: 0 }}>
            {applyInlineRules(inner, `${keyPrefix}i`)}
          </Tag>
        );
      }

      if (after) parts.push(...applyInlineRules(after, `${keyPrefix}a`));
      return parts;
    }
  }

  // No match – return plain text.
  return [text];
}

/* ------------------------------------------------------------------ */
/*  Block-level parser – handles lists, blockquotes, line breaks      */
/* ------------------------------------------------------------------ */

function parseBlocks(text) {
  // Normalise literal "\n" sequences (sometimes sent as escaped text).
  const normalised = text.replace(/\\n/g, '\n');
  const lines = normalised.split('\n');
  const blocks = [];
  let key = 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimStart();

    // Bullet list:  "* item" or "- item"
    const bulletMatch = trimmed.match(/^([*-])\s+(.+)/);
    if (bulletMatch) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trimStart();
        const m = t.match(/^([*-])\s+(.+)/);
        if (!m) break;
        items.push(m[2]);
        i++;
      }
      const bulletKey = key++;
      blocks.push(
        <ul key={bulletKey} style={{ margin: '4px 0', paddingLeft: 20 }}>
          {items.map((item, j) => (
            <li key={j}>{parseInline(item, `li${bulletKey}${j}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list:  "1. item"
    const numMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (numMatch) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trimStart();
        const m = t.match(/^(\d+)\.\s+(.+)/);
        if (!m) break;
        items.push(m[2]);
        i++;
      }
      const olKey = key++;
      blocks.push(
        <ol key={olKey} style={{ margin: '4px 0', paddingLeft: 20 }}>
          {items.map((item, j) => (
            <li key={j}>{parseInline(item, `ol${olKey}${j}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Blockquote:  "> text"
    const quoteMatch = trimmed.match(/^>\s?(.*)/);
    if (quoteMatch) {
      const quoteLines = [];
      while (i < lines.length) {
        const t = lines[i].trimStart();
        const m = t.match(/^>\s?(.*)/);
        if (!m) break;
        quoteLines.push(m[1]);
        i++;
      }
      const quoteKey = key++;
      blocks.push(
        <Box
          component="blockquote"
          key={quoteKey}
          // Relativo à cor do texto: funciona no balão recebido e no enviado (primary).
          sx={{
            margin: '4px 0',
            paddingLeft: 1,
            borderLeft: '3px solid',
            borderColor: 'color-mix(in srgb, currentColor 30%, transparent)',
            opacity: 0.8,
          }}
        >
          {quoteLines.map((ql, j) => (
            <React.Fragment key={j}>
              {j > 0 && <br />}
              {parseInline(ql, `q${quoteKey}${j}`)}
            </React.Fragment>
          ))}
        </Box>
      );
      continue;
    }

    // Empty line → spacer
    if (trimmed === '') {
      blocks.push(<br key={key++} />);
      i++;
      continue;
    }

    // Regular line — inline formatting only
    const lineKey = key++;
    blocks.push(
      <React.Fragment key={lineKey}>
        {lineKey > 0 && <br />}
        {parseInline(trimmed, `p${lineKey}`)}
      </React.Fragment>
    );
    i++;
  }

  return blocks;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function WhatsAppFormattedText({ text }) {
  if (!text) return null;
  return <>{parseBlocks(text)}</>;
}
